import type { ClientCase } from './clientCases'

export const clientCasesPart2: ClientCase[] = [
  {
    slug: 'fysiotherapiepraktijk-cg',
    clientName: 'Fysiotherapiepraktijk CG',
    sector: 'Fysiotherapie',
    shortDescription:
      'Een complete praktijkwebsite voor Fysiotherapiepraktijk CG in Julianadorp, met veertien behandelpagina\'s, een gekoppeld patiëntenportaal, vacatures en een blog. Ons grootste zorgproject tot nu toe.',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/8F1mimE51jKZzEj15bBqry/Trainingszaal_cardio_liggend.jpg',
    metaDescription:
      'Lees de case van Fysiotherapiepraktijk CG in Julianadorp. Een uitgebreide praktijkwebsite met veertien behandelpagina\'s, patiëntenportaal, vacaturemodule en blog, gebouwd door Noord Digital.',
    content: [
      {
        type: 'paragraph',
        content:
          'Fysiotherapiepraktijk CG zit in Julianadorp en begeleidt mensen bij revalidatie en herstel. Een hecht team van vijf, meer dan vijfentwintig jaar ervaring, ruim zevenhonderdvijftig trajecten per jaar en een trainingszaal van tweehonderd vierkante meter. De praktijk is aangesloten bij zeven gespecialiseerde netwerken, waaronder Netwerk Artrose, Netwerk Oncologie en Netwerk Traumarevalidatie Noordwest.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Een praktijk met veertien specialisaties heeft een probleem dat de meeste zorgaanbieders niet hebben: er is te veel om te vertellen. Iemand die zoekt op oedeemfysiotherapie wil niet eerst door een algemeen verhaal over fysiotherapie heen. Tegelijk moest de site rust uitstralen, want de bezoeker is vaak iemand die herstelt van een operatie of leert leven met een chronische aandoening. Daarbij speelde nog iets anders: de praktijk zocht actief nieuwe collega\'s, dus werving moest een volwaardige plek krijgen zonder de patient uit beeld te duwen.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Veertien eigen behandelpagina\'s opgezet, van oncologiefysiotherapie tot dry needling en littekentherapie, zodat elke specialisatie zelfstandig vindbaar is in Google.',
          'Het patiëntenportaal van Intramed rechtstreeks gekoppeld, zodat patiënten dag en nacht afspraken kunnen beheren, hun dossier kunnen inzien en vragenlijsten kunnen invullen.',
          'Een vacaturemodule ingebouwd met eigen pagina\'s per functie, zodat werving via de site loopt in plaats van via losse advertenties.',
          'De trainingszaal als onderscheidend punt naar voren gehaald met concrete cijfers, omdat revalidatiecapaciteit precies is wat deze praktijk anders maakt.',
          'Een blog opgezet waarin de therapeuten uitleg geven over herstel na een knieoperatie, sporten met artrose en oefeningen bij rugklachten.',
          'Kwaliteitssignalen zichtbaar gemaakt: BIG-registratie, KNGF-aansluiting en de zeven zorgnetwerken waar de praktijk bij hoort.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Veertien behandelpagina\'s die ieder op hun eigen zoekterm gevonden kunnen worden, in plaats van één algemene dienstenpagina.',
          'Patiënten die zelf hun afspraken regelen via het portaal, wat de administratieve druk op de praktijk verlaagt.',
          'Werving die via de eigen site loopt, met een duidelijk beeld van hoe het is om bij CG te werken.',
          'Een uitstraling die warm en deskundig tegelijk is, passend bij een praktijk die aanvoelt als thuis.',
        ],
      },
      {
        type: 'callout',
        content:
          'Bij een praktijk met veertien specialisaties is de kunst niet om alles te tonen, maar om iedereen meteen op de juiste pagina te laten landen.',
      },
      {
        type: 'paragraph',
        content:
          'Fysiotherapiepraktijk CG heeft nu een website die de breedte van de praktijk laat zien zonder de bezoeker te overladen, en die tegelijk werkt als wervingskanaal voor nieuwe collega\'s.',
      },
    ],
    website: 'https://www.fysiotherapiepraktijkcg.nl',
  },
  {
    slug: 'de-haar-school',
    clientName: 'De Haar School',
    sector: 'Opleiding en kapsalon',
    shortDescription:
      'Drie bedrijfstakken op één site voor De Haar School in Obdam: vakopleidingen voor kappers, workshops voor particulieren en de kapsalon van Miranda Mulder, wereldkampioen haarstyling.',
    heroImage:
      'https://orangered-salamander-193156.hostingersite.com/wp-content/uploads/2026/08/download-scaled-e1785850278460.jpg',
    metaDescription:
      'Lees de case van De Haar School in Obdam. Een website die drie bedrijfstakken samenbrengt: trainingen voor vakgenoten, workshops voor particulieren en de kapsalon van Miranda Mulder.',
    content: [
      {
        type: 'paragraph',
        content:
          'Miranda Mulder groeide op boven de kapsalon van haar ouders, werkt sinds 1987 in het vak en werd in 1994 wereldkampioen haarstyling met het Nederlands jeugdteam. Sinds 2001 geeft ze les. Toen steeds meer mensen haar vroegen om uitleg over vlechten en opsteken, ontstond De Haar School in Obdam.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'De Haar School is eigenlijk drie bedrijven in één. Vakopleidingen voor kappers en bruidstylisten, workshops voor particulieren die zelf willen leren vlechten, en een gewone kapsalon voor knippen, kleuren en bruidskapsels. Die drie doelgroepen willen totaal verschillende dingen weten en hebben een totaal verschillend prijsniveau, van twintig euro voor een workshop tot tweehonderdvijfentachtig voor een driedaagse master. Alles op één hoop gooien zou alle drie de takken schaden.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'De site opgebouwd in genummerde blokken, waarbij trainingen, workshops en kapsalon elk hun eigen duidelijk afgebakende zone krijgen met eigen toon en eigen prijspresentatie.',
          'Per training en workshop een eigen pagina, zodat iemand die zoekt op een kniptraining of een vlechtworkshop rechtstreeks op de juiste plek landt.',
          'Prijzen volledig transparant in beeld gebracht, inclusief training op locatie met kilometervergoeding, omdat vakgenoten daar vooraf duidelijkheid over willen.',
          'Het verhaal van Miranda als anker gebruikt: het wereldkampioenschap van 1994 en de jaren sinds 1987 zijn het bewijs waar de hele propositie op rust.',
          'Voor de kapsalon een directe WhatsApp-route ingebouwd, omdat die klant niet wil mailen maar gewoon een afspraak wil.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Drie doelgroepen die op dezelfde site terechtkunnen zonder elkaar in de weg te zitten.',
          'Een eigen pagina per training en workshop, waardoor het aanbod zelfstandig vindbaar wordt.',
          'Vakmanschap dat direct zichtbaar is via jaartallen en het wereldkampioenschap, in plaats van via loze claims.',
          'Een salonroute die in twee tikken bij een afspraak uitkomt.',
        ],
      },
      {
        type: 'callout',
        content:
          'Als één ondernemer drie bedrijven runt, moet de website die drie werelden scheiden zonder het merk te versnipperen.',
      },
      {
        type: 'paragraph',
        content:
          'De Haar School heeft nu een site waar een kapper, een bruid en iemand die gewoon geknipt wil worden alle drie het gevoel hebben dat de pagina voor hen is gemaakt.',
      },
    ],
    website: 'https://dehaarschool.nl',
  },
  {
    slug: '5-stars-events',
    clientName: '5* Stars Events',
    sector: 'Horeca uitzendbureau',
    shortDescription:
      'Een nieuwe website voor horeca-uitzendbureau 5* Stars Events met tien eigen rolpagina\'s, zodat werving en acquisitie allebei via de site lopen. Zo tevreden dat we nu ook de visitekaartjes en e-mailfooter mogen maken.',
    heroImage:
      'https://5starsevents.nl/wp-content/uploads/2026/07/5-stars-events-barpersoneel-horeca.jpg-1.png',
    metaDescription:
      'Lees de case van 5* Stars Events. Een website voor een horeca-uitzendbureau in Noord-Holland en Utrecht, met tien rolpagina\'s en een dubbele funnel voor personeel en opdrachtgevers.',
    content: [
      {
        type: 'paragraph',
        content:
          '5* Stars Events is een high-end uitzendbureau voor horeca, catering en evenementen in Noord-Holland en Utrecht. Ze leveren hostesses, bartenders, koks, festivalcrew en beurspersoneel aan horecazaken, cateraars en evenementen.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Een uitzendbureau heeft een probleem dat de meeste bedrijven niet kennen: het moet twee compleet verschillende markten tegelijk bedienen. Aan de ene kant horecapersoneel dat werk zoekt en vooral wil weten wat het betaalt, hoe flexibel het is en of het bureau te vertrouwen is. Aan de andere kant opdrachtgevers die snel representatief personeel nodig hebben. De oude website bediende geen van beide goed. Daarbij was de belangrijkste vraag hoe je vacatures aanbiedt zonder dat de site een saaie vacaturebank wordt.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'De site opgesplitst in twee duidelijke routes vanaf de eerste sectie: kom werken bij ons, en huur personeel in.',
          'Tien eigen rolpagina\'s gebouwd, van bartender en hostess tot verkeersregelaar en projectmanager, zodat elke functie zelfstandig vindbaar is voor werkzoekenden.',
          'Het contactformulier voorzien van routing, zodat de bezoeker eerst aangeeft of hij werk zoekt, personeel zoekt of een algemene vraag heeft.',
          'Ervaringen van het eigen team op de site gezet, omdat werkzoekenden in deze branche vooral afgaan op wat collega\'s zeggen over betaling en sfeer.',
          'De zes functiegroepen visueel gepresenteerd, zodat iemand in één oogopslag ziet welk type werk bij hem past.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Twee doelgroepen die vanaf de eerste seconde hun eigen route hebben.',
          'Tien rolpagina\'s die ieder op hun eigen zoekterm gevonden kunnen worden, wat voor een uitzendbureau het hele verdienmodel raakt.',
          'Aanvragen die vooraf al gesorteerd binnenkomen dankzij de routing in het formulier.',
          'Een vervolgopdracht voor visitekaartjes en een e-mailfooter, omdat de klant zo tevreden was over de site.',
        ],
      },
      {
        type: 'callout',
        content:
          'Bij een uitzendbureau is de vacaturepagina geen bijzaak. Het is het product.',
      },
      {
        type: 'paragraph',
        content:
          '5* Stars Events werft nu personeel en opdrachtgevers via dezelfde site, zonder dat de een de ander in de weg zit. En het vertrouwen ging verder dan de website: we verzorgen inmiddels ook de vormgeving van hun visitekaartjes en e-mailfooter.',
      },
    ],
    website: 'https://5starsevents.nl',
  },
  {
    slug: 'aurora-swimming',
    clientName: 'Aurora Swimming',
    sector: 'Zwemvereniging',
    shortDescription:
      'Een gesponsorde website voor Aurora Swimming, een zwemclub die haar zwemmers professioneel wil begeleiden in en buiten het zwembad. Een aanpak die in de zwemwereld nog nauwelijks bestaat.',
    heroImage:
      'https://auroraswimming.nl/wp-content/uploads/2026/08/aurora-coach-badrand.webp',
    metaDescription:
      'Lees de case van Aurora Swimming. Noord Digital sponsorde de website van deze talentgerichte zwemclub, die zwemmers breed opleidt volgens het Athletic Skills Model.',
    content: [
      {
        type: 'paragraph',
        content:
          'Aurora Swimming is een wedstrijdzwemclub in oprichting in Oosterhout. De club doet iets wat in de Nederlandse zwemwereld nog nauwelijks gebeurt: zwemmers niet alleen in het water trainen, maar ze breed atletisch opleiden volgens het Athletic Skills Model. Naast de zwemtraining staat er wekelijks een gymtraining op het programma, gericht op motoriek, beweegvaardigheid en blessurepreventie.',
      },
      { type: 'heading', content: 'Waarom wij dit sponsorden' },
      {
        type: 'paragraph',
        content:
          'Deze website hebben wij gesponsord, en daar zijn we trots op. Wat Aurora doet raakt aan iets fundamenteels: jonge sporters worden in veel clubs te vroeg gespecialiseerd, met blessures en uitval als gevolg. De aanpak van Aurora, met professionele coaches langs de badrand en een brede atletische basis, zorgt voor een gezonder en beter traject voor de sporters. Zoiets verdient een platform dat het serieus neemt.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Een vereniging in oprichting heeft geen geschiedenis om op te leunen en geen ledenbestand om naar te verwijzen. De site moest dus vertrouwen opbouwen op basis van de inhoud van de methode. Daarbij moest hij drie dingen tegelijk doen: ouders overtuigen om hun kind een proeftraining te laten doen, sponsoren aantrekken voor een club die nog moet groeien, en donaties mogelijk maken.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'De methode centraal gezet in plaats van resultaten, met het Athletic Skills Model en de brede opleiding als inhoudelijke onderbouwing.',
          'Een groeipad uitgetekend van Onder 10 via talentontwikkeling naar wedstrijdzwemmen, zodat ouders zien waar hun kind over vijf jaar staat.',
          'De gratis proeftraining als laagdrempelige eerste stap prominent in beeld gebracht.',
          'Een sponsorsectie met open plekken ingericht, zodat de club nieuwe partners kan werven en die direct zichtbaar worden.',
          'Een donatiemodule ingebouwd met iDEAL, Wero, Bancontact en creditcard.',
          'Volledige transparantie over trainingstijden, locaties en tarieven, omdat ouders dat als eerste willen weten.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een club in oprichting die online overkomt als een serieuze, doordachte organisatie.',
          'Een verhaal dat ouders uitlegt waarom brede opleiding beter is dan vroege specialisatie.',
          'Een sponsor- en donatiestructuur die meegroeit met de club.',
          'Een plek waar de zwemwereld kan zien hoe professionele begeleiding er ook uit kan zien.',
        ],
      },
      {
        type: 'callout',
        content:
          'Soms is de beste investering geen factuur. Als een club iets doet wat de sport vooruithelpt, mag daar een goede website bij horen.',
      },
      {
        type: 'paragraph',
        content:
          'Aurora Swimming heeft een platform dat past bij de ambitie: professionele begeleiding voor zwemmers, in en buiten het bad. Noord Digital staat er als hoofdsponsor en digitale partner op vermeld.',
      },
    ],
    website: 'https://auroraswimming.nl',
  },
  {
    slug: 'ars-cleanex',
    clientName: 'Ars Cleanex',
    sector: 'Schoonmaker',
    shortDescription:
      'Een strakke onepager voor Ars Cleanex in Genk, België. Onze eerste klant over de grens, en het bewijs dat een goede onepager voor een zzp-er genoeg is om serieus genomen te worden.',
    heroImage:
      'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=500&fit=crop',
    metaDescription:
      'Lees de case van Ars Cleanex uit Genk. Een heldere onepager voor een zelfstandig schoonmaakbedrijf, met zes diensten, een werkwijze in vier stappen en directe offerteaanvraag.',
    content: [
      {
        type: 'paragraph',
        content:
          'Ars Cleanex is een zelfstandig schoonmaakbedrijf uit Genk in Belgisch Limburg. De eigenaresse maakt kantoorpanden, vakantieparken en woningen schoon, met ruim zeven jaar ervaring en meer dan vijfhonderd panden achter de rug. Onze eerste klant buiten Nederland.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'In de schoonmaakbranche is de concurrentie groot en de differentiatie klein. Iedereen zegt grondig te zijn. Voor een zelfstandige zonder groot marketingbudget is de vraag dus hoe je in een paar seconden duidelijk maakt dat jij anders werkt. Daarbij speelde dat de doelgroep breed is: kantoren, vakantieparken, grootaannemers en particulieren tegelijk, terwijl die allemaal iets anders zoeken.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Gekozen voor een onepager in plaats van een uitgebreide site, omdat de bezoeker hier maar een paar dingen wil weten en snel wil bellen.',
          'Zes diensten helder naast elkaar gezet, van kantoorschoonmaak en wisselschoonmaak voor vakantieparken tot opleveringsschoonmaak na verbouwing.',
          'De werkwijze in vier stappen uitgeschreven, zodat een opdrachtgever vooraf weet hoe het traject verloopt en wat hij mag verwachten.',
          'De persoonlijke kant naar voren gehaald, omdat een zzp-er juist wint op benaderbaarheid waar grote schoonmaakbedrijven dat verliezen.',
          'Direct bellen en offerte aanvragen op elke schermhoogte bereikbaar gemaakt.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een professionele uitstraling die niet onderdoet voor grotere schoonmaakbedrijven in de regio.',
          'Een heldere scheiding tussen zakelijke en particuliere diensten, zonder dat de site onoverzichtelijk wordt.',
          'Een lage drempel om contact op te nemen, met bellen en het formulier altijd binnen bereik.',
          'Onze eerste opdracht in België, waarmee het werkgebied van Noord Digital verder reikt dan Noord-Holland.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een zzp-er hoeft geen tien pagina\'s. Eén pagina die alles beantwoordt en makkelijk laat bellen is vaak sterker.',
      },
      {
        type: 'paragraph',
        content:
          'Ars Cleanex heeft nu een online visitekaartje dat past bij het vakmanschap, en laat zien dat een goed opgezette onepager voor een zelfstandige genoeg kan zijn.',
      },
    ],
    website: 'https://arscleanex.be',
  },
]
